<script setup lang="ts">
// Wizard step 1 — name, description, platform, accounts (`DataExportStep1.vue`, 456 ln).
//
// Legacy took 20 props and raised 11 emits so the page host could own every field; here the step
// reads and writes `store.draft` directly, which is what collapses the prop surface. The eight
// copy-pasted platform blocks collapse into one `PlatformAccountPicker` loop.
//
// No error state: the platform catalog is a pure read over module 12's already-hydrated
// integration map, so there is no fetch here that can fail and nothing to retry. The three states
// that DO exist are modelled — hydrating, entitled-but-nothing-connected, and ready.
import { computed } from 'vue'

import { usePlatformAccounts } from '../../../composables/usePlatformAccounts'
import type { ExportJobDraft, ExportPlatform } from '../../../models/exportJob.types'
import { useDataExportStore } from '../../../stores/dataExportStore'
import PlatformAccountPicker from '../PlatformAccountPicker.vue'

import { useI18n } from '@/shared/i18n'
import { Checkbox, Input, PlatformLogo, Skeleton, Textarea } from '@/shared/ui'

const { t } = useI18n()
const store = useDataExportStore()
const catalog = usePlatformAccounts()

const name = draftText('name')
const description = draftText('description')

const selectedPlatforms = computed<readonly ExportPlatform[]>(() => store.draft?.platforms ?? [])
const pickers = computed(() =>
  catalog.availablePlatforms.value.filter((descriptor) =>
    selectedPlatforms.value.includes(descriptor.platform),
  ),
)

function draftText<K extends 'name' | 'description'>(key: K) {
  return computed<string>({
    get: () => store.draft?.[key] ?? '',
    set: (value) => {
      store.updateDraft({ [key]: value } as Partial<ExportJobDraft>)
    },
  })
}

/**
 * `handlePlatformSelection` (`DataExportCreate.vue:392-400`) is single-select despite the checkbox
 * chrome: it REPLACES the array with `[item]` on every pick and empties it on a second click. The
 * behaviour is preserved deliberately — `/create-export-job` has only ever received single-element
 * `platforms` arrays, and widening it here would ship a wire shape the backend has never seen.
 *
 * `calculateSelectedAccounts` (`:474-503`) then drops the accounts of every de-selected platform
 * and invalidates the template, which is platform-scoped.
 */
function togglePlatform(platform: ExportPlatform): void {
  const draft = store.draft
  if (!draft) return
  const platforms: ExportPlatform[] = draft.platforms.includes(platform) ? [] : [platform]
  const selectedAccounts: ExportJobDraft['selectedAccounts'] = {}
  for (const kept of platforms) {
    const accounts = draft.selectedAccounts[kept]
    if (accounts) selectedAccounts[kept] = accounts
  }
  store.updateDraft({ platforms, selectedAccounts, template: null })
}
</script>

<template>
  <section class="export-source grid gap-md" :aria-label="t('dataExport.source.title')">
    <Input
      v-model="name"
      :label="t('dataExport.source.nameLabel')"
      :placeholder="t('dataExport.source.namePlaceholder')"
    />
    <Textarea
      v-model="description"
      :label="t('dataExport.source.descriptionLabel')"
      :placeholder="t('dataExport.source.descriptionPlaceholder')"
      :rows="3"
    />

    <fieldset class="export-source__group grid gap-sm m-0 p-md border border-border rounded-md">
      <legend>{{ t('dataExport.source.platformsLabel') }}</legend>

      <div v-if="!catalog.isReady.value" class="export-source__skeleton grid gap-xs">
        <Skeleton height="3rem" />
        <Skeleton height="3rem" />
        <Skeleton height="3rem" />
      </div>

      <p
        v-else-if="catalog.availablePlatforms.value.length === 0"
        class="export-source__empty m-0 text-muted"
      >
        {{ t('dataExport.source.platformsEmpty') }}
      </p>

      <ul v-else class="export-source__platforms grid gap-xs m-0 p-0 list-none">
        <li v-for="descriptor in catalog.availablePlatforms.value" :key="descriptor.platform">
          <Checkbox
            :model-value="selectedPlatforms.includes(descriptor.platform)"
            :aria-label="t(descriptor.labelKey)"
            @update:model-value="togglePlatform(descriptor.platform)"
          />
          <PlatformLogo :platform="descriptor.platform" :size="20" />
          <span>{{ t(descriptor.labelKey) }}</span>
        </li>
      </ul>
    </fieldset>

    <fieldset
      v-if="pickers.length > 0"
      class="export-source__group grid gap-sm m-0 p-md border border-border rounded-md"
    >
      <legend>{{ t('dataExport.source.accountsLabel') }}</legend>
      <PlatformAccountPicker
        v-for="descriptor in pickers"
        :key="descriptor.platform"
        :descriptor="descriptor"
      />
    </fieldset>
  </section>
</template>

<style scoped>
.export-source .export-source__group legend {
  padding-inline: var(--ui-space-2xs);
  color: var(--ui-color-subtle);
  font-weight: 600;
}
.export-source .export-source__platforms li {
  display: flex;
  align-items: center;
  gap: var(--ui-space-sm);
  padding: var(--ui-space-sm);
  border: 1px solid var(--ui-color-border);
  border-radius: var(--ui-radius-sm);
}
</style>
